import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEPARTMENTS } from '@flow-doc/shared';
import { RecordExtensionInstallGuide } from './sop/RecordExtensionInstallGuide';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { useExtensionInstalled } from '../hooks/useExtensionInstalled';
import { useExtensionRecordingBridge } from '../hooks/useExtensionRecordingBridge';
import { apiFetch } from '../utils/api';
import { dashboardPanelHref } from '../constants/dashboardPanels';
import { getProductLabelSop } from '../constants/productLabels';
const USER_ROLES = [
    { value: 'agent', label: 'Agent' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Admin' },
    { value: 'other', label: 'Other' },
];
const EXTENSION_INSTALL_URL = process.env.REACT_APP_EXTENSION_INSTALL_URL;
function derivePhase(args) {
    const { source, view, extensionInstalled, snapshot, completedFlowId } = args;
    if (source === 'document' || view === 'library')
        return 'library-first';
    if (view === 'install')
        return 'not-installed';
    if (extensionInstalled === null)
        return 'checking';
    if (extensionInstalled === false)
        return 'not-installed';
    if (completedFlowId)
        return 'done';
    if (!snapshot)
        return 'idle';
    switch (snapshot.state) {
        case 'armed':
            return 'armed';
        case 'recording':
            return 'recording';
        case 'stopping':
            return 'stopping';
        case 'uploading':
            return 'uploading';
        case 'idle':
        default:
            return 'idle';
    }
}
function formatDuration(ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}
export default function CreateSOPModal({ open, source = 'sidebar', initialView, onClose, }) {
    const navigate = useNavigate();
    const extensionInstalled = useExtensionInstalled(open);
    const enabled = open && extensionInstalled === true;
    const bridge = useExtensionRecordingBridge({ enabled });
    const { snapshot } = bridge;
    const resolvedInitialView = initialView ?? (source === 'document' ? 'library' : 'recording');
    const [view, setView] = useState(resolvedInitialView);
    const [department, setDepartment] = useState('retail');
    const [category, setCategory] = useState('point_of_sale');
    const [userRole, setUserRole] = useState('agent');
    const [localError, setLocalError] = useState(null);
    const [completedFlowId, setCompletedFlowId] = useState(null);
    const [armBusy, setArmBusy] = useState(false);
    const [stopBusy, setStopBusy] = useState(false);
    const [outcomeResult, setOutcomeResult] = useState('success');
    const [outcomeReason, setOutcomeReason] = useState('');
    const prevArmedFingerprintRef = useRef(null);
    useEffect(() => {
        if (!open) {
            setLocalError(null);
            setCompletedFlowId(null);
            setArmBusy(false);
            setStopBusy(false);
            setOutcomeResult('success');
            setOutcomeReason('');
            setView(resolvedInitialView);
        }
    }, [open, resolvedInitialView]);
    // Adopt armed prefs from the extension (e.g., after a service-worker restart).
    useEffect(() => {
        const armed = snapshot?.armed;
        if (!armed) {
            prevArmedFingerprintRef.current = null;
            return;
        }
        const fingerprint = `${armed.department}|${armed.category}|${armed.userRole}`;
        if (fingerprint === prevArmedFingerprintRef.current)
            return;
        prevArmedFingerprintRef.current = fingerprint;
        setDepartment(armed.department);
        setCategory(armed.category);
        setUserRole(armed.userRole);
    }, [snapshot?.armed]);
    // Capture the latest uploaded flowId.
    useEffect(() => {
        if (snapshot?.flowId) {
            setCompletedFlowId(snapshot.flowId);
        }
    }, [snapshot?.flowId]);
    // Keep category valid when department changes.
    useEffect(() => {
        const departmentSection = DEPARTMENTS[department];
        if (!departmentSection)
            return;
        const categories = departmentSection.categories;
        if (categories.length === 0)
            return;
        if (!categories.some((c) => c.id === category)) {
            setCategory(categories[0].id);
        }
    }, [department, category]);
    const phase = derivePhase({
        source,
        view,
        extensionInstalled,
        snapshot,
        completedFlowId,
    });
    const showError = localError ?? snapshot?.error ?? null;
    const handleArm = async () => {
        setLocalError(null);
        setArmBusy(true);
        try {
            const uploadToken = await apiFetch('/api/auth/extension-upload-token', {
                method: 'POST',
            });
            const prefs = {
                department,
                category,
                userRole,
                uploadToken: uploadToken.token,
            };
            const ack = await bridge.arm(prefs);
            if (!ack.ok) {
                setLocalError(ack.error ?? 'Failed to arm recording');
            }
        }
        catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Failed to arm recording');
        }
        finally {
            setArmBusy(false);
        }
    };
    const handleCancelArmed = () => {
        bridge.clearArmed();
    };
    const handleStop = async () => {
        setLocalError(null);
        setStopBusy(true);
        try {
            const outcome = {
                result: outcomeResult,
                ...(outcomeReason.trim() ? { reason: outcomeReason.trim() } : {}),
            };
            const ack = await bridge.stop(outcome);
            if (!ack.ok) {
                setLocalError(ack.error ?? 'Failed to stop recording');
            }
            else if (ack.flowId) {
                setCompletedFlowId(ack.flowId);
            }
        }
        catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Failed to stop recording');
        }
        finally {
            setStopBusy(false);
        }
    };
    const handleOpenLibrary = () => {
        onClose();
        navigate(dashboardPanelHref('library'));
    };
    const handleViewFlow = () => {
        if (!completedFlowId)
            return;
        onClose();
        navigate(`/flow/${completedFlowId}`);
    };
    const handleRecordAnother = () => {
        setCompletedFlowId(null);
        bridge.refresh();
        setView('recording');
    };
    const handleShowInstall = () => {
        setView('install');
    };
    const trainingLabel = getProductLabelSop();
    const title = phase === 'not-installed'
        ? 'Install the Capture Extension'
        : phase === 'library-first'
            ? trainingLabel
            : phase === 'done'
                ? 'Recording complete'
                : phase === 'checking'
                    ? trainingLabel
                    : 'Record a flow';
    return (_jsx(Modal, { open: open, onClose: onClose, title: title, size: "md", footer: renderFooter({
            phase,
            stopBusy,
            armBusy,
            onClose,
            onArm: handleArm,
            onCancelArmed: handleCancelArmed,
            onStop: handleStop,
            onOpenLibrary: handleOpenLibrary,
            onViewFlow: handleViewFlow,
            onRecordAnother: handleRecordAnother,
            onShowInstall: handleShowInstall,
        }), children: _jsxs("div", { className: "create-sop-modal", children: [showError && (_jsxs("div", { role: "alert", className: "create-sop-status", style: { color: 'var(--color-warning-red, #b91c1c)', borderColor: 'var(--color-warning-red, #b91c1c)' }, children: [_jsx("span", { "aria-hidden": "true", children: "!" }), _jsx("span", { children: showError })] })), phase === 'checking' && (_jsx("p", { className: "create-sop-lede", children: "Checking Cricket Studio extension status..." })), phase === 'not-installed' && _jsx(RecordExtensionInstallGuide, { extensionInstallUrl: EXTENSION_INSTALL_URL }), phase === 'library-first' && (_jsx("p", { className: "create-sop-lede", children: `Use the Content Library to select a recorded flow and generate a draft ${getProductLabelSop()}.` })), (phase === 'idle' || phase === 'armed') && (_jsx(RecordingForm, { department: department, category: category, userRole: userRole, onChangeDepartment: setDepartment, onChangeCategory: setCategory, onChangeUserRole: setUserRole, phase: phase, disabled: armBusy || phase === 'armed' })), (phase === 'recording' || phase === 'stopping') && snapshot && (_jsx(RecordingLive, { snapshot: snapshot, outcomeResult: outcomeResult, outcomeReason: outcomeReason, onOutcomeResult: setOutcomeResult, onOutcomeReason: setOutcomeReason, stopBusy: stopBusy || phase === 'stopping' })), phase === 'uploading' && (_jsxs("div", { className: "create-sop-lede", children: [_jsx("p", { style: { margin: 0 }, children: "Uploading your flow to Cricket Studio..." }), _jsx("p", { style: { margin: '4px 0 0' }, children: "This usually takes a few seconds." })] })), phase === 'done' && (_jsx("div", { className: "create-sop-lede", children: _jsx("p", { style: { margin: 0 }, children: `Your flow uploaded successfully. Open it to generate a draft ${getProductLabelSop()} from the Library.` }) }))] }) }));
}
function RecordingForm({ department, category, userRole, onChangeDepartment, onChangeCategory, onChangeUserRole, phase, disabled, }) {
    const departments = Object.keys(DEPARTMENTS);
    const categories = DEPARTMENTS[department]?.categories ?? [];
    return (_jsxs("div", { className: "create-sop-modal", "data-testid": "create-sop-recording-form", children: [_jsx("p", { className: "create-sop-lede", children: phase === 'armed'
                    ? 'Your session is ready. Switch to the tab you want to capture, open the Cricket Studio toolbar menu, then tap Start Recording there.'
                    : 'Open the tab you want to record, pick the context, and start recording.' }), _jsxs("div", { className: "create-sop-status", children: [_jsx("span", { className: "create-sop-status-dot is-installed", "aria-hidden": "true" }), _jsx("span", { children: "Cricket Studio extension is connected." })] }), _jsxs("fieldset", { disabled: disabled, style: { border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--spacing-3)' }, children: [_jsxs("label", { style: { display: 'grid', gap: 4 }, children: [_jsx("span", { style: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }, children: "Department" }), _jsx("select", { value: department, onChange: (e) => onChangeDepartment(e.target.value), children: departments.map((dept) => (_jsx("option", { value: dept, children: DEPARTMENTS[dept].name }, dept))) })] }), _jsxs("label", { style: { display: 'grid', gap: 4 }, children: [_jsx("span", { style: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }, children: "Category" }), _jsx("select", { value: category, onChange: (e) => onChangeCategory(e.target.value), children: categories.map((cat) => (_jsx("option", { value: cat.id, children: cat.name }, cat.id))) })] }), _jsxs("label", { style: { display: 'grid', gap: 4 }, children: [_jsx("span", { style: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }, children: "Your role" }), _jsx("select", { value: userRole, onChange: (e) => onChangeUserRole(e.target.value), children: USER_ROLES.map((role) => (_jsx("option", { value: role.value, children: role.label }, role.value))) })] })] }), phase === 'armed' && (_jsxs("div", { className: "create-sop-status", style: {
                    background: 'var(--color-surface)',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 'var(--spacing-2)',
                }, "aria-live": "polite", children: [_jsx("strong", { style: { color: 'var(--color-text-primary)' }, children: "Session armed. One more step:" }), _jsx("span", { children: "Click the Cricket Studio icon in your browser toolbar while you are on the tab you want to record, then choose Start Recording in the popup." })] }))] }));
}
function RecordingLive({ snapshot, outcomeResult, outcomeReason, onOutcomeResult, onOutcomeReason, stopBusy, }) {
    const stats = snapshot.stats ?? { duration: 0, eventCount: 0, screenshotCount: 0 };
    return (_jsxs("div", { className: "create-sop-modal", "data-testid": "create-sop-recording-live", children: [_jsxs("div", { className: "create-sop-status", style: {
                    color: 'var(--color-text-primary)',
                    borderColor: 'var(--color-cricket-green, #60a630)',
                }, children: [_jsx("span", { className: "create-sop-status-dot is-installed", "aria-hidden": "true" }), _jsx("span", { children: "Recording in progress on the active tab." }), _jsx("span", { className: "create-sop-pill", children: formatDuration(stats.duration) })] }), _jsxs("div", { style: {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 'var(--spacing-3)',
                }, children: [_jsx(LiveStat, { label: "Events captured", value: stats.eventCount }), _jsx(LiveStat, { label: "Screenshots", value: stats.screenshotCount })] }), _jsxs("fieldset", { disabled: stopBusy, style: { border: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--spacing-2)' }, children: [_jsx("span", { style: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }, children: "Outcome when you stop" }), _jsx("div", { style: { display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }, children: ['success', 'partial', 'failed'].map((result) => (_jsxs("label", { style: {
                                display: 'inline-flex',
                                gap: 6,
                                alignItems: 'center',
                                padding: '4px 10px',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                            }, children: [_jsx("input", { type: "radio", name: "create-sop-outcome", value: result, checked: outcomeResult === result, onChange: () => onOutcomeResult(result) }), result] }, result))) }), _jsxs("label", { style: { display: 'grid', gap: 4 }, children: [_jsx("span", { style: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }, children: "Notes (optional)" }), _jsx("textarea", { rows: 2, value: outcomeReason, placeholder: "What happened? Any context worth keeping?", onChange: (e) => onOutcomeReason(e.target.value) })] })] })] }));
}
function LiveStat({ label, value }) {
    return (_jsxs("div", { style: {
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-3)',
            display: 'grid',
            gap: 2,
            background: 'var(--color-background)',
        }, children: [_jsx("span", { style: {
                    fontSize: 'var(--text-h4-size)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--color-text-primary)',
                }, children: value }), _jsx("span", { style: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }, children: label })] }));
}
function renderFooter(args) {
    const { phase, armBusy, stopBusy, onClose, onArm, onCancelArmed, onStop, onOpenLibrary, onViewFlow, onRecordAnother, onShowInstall, } = args;
    const closeBtn = (_jsx(Button, { variant: "secondary", onClick: onClose, children: "Close" }));
    switch (phase) {
        case 'checking':
            return (_jsxs(_Fragment, { children: [closeBtn, _jsx(Button, { variant: "secondary", onClick: onOpenLibrary, children: "Open Library" })] }));
        case 'not-installed':
            return (_jsxs(_Fragment, { children: [closeBtn, _jsx(Button, { variant: "secondary", onClick: onOpenLibrary, children: "Open Library" }), EXTENSION_INSTALL_URL ? (_jsx("a", { className: "btn-ui btn-primary btn-size-md", href: EXTENSION_INSTALL_URL, target: "_blank", rel: "noopener noreferrer", children: _jsx("span", { children: "Install link" }) })) : null] }));
        case 'library-first':
            return (_jsxs(_Fragment, { children: [closeBtn, _jsx(Button, { variant: "secondary", onClick: onShowInstall, children: "Install Extension" }), _jsx(Button, { variant: "primary", onClick: onOpenLibrary, children: "Open Library" })] }));
        case 'idle':
            return (_jsxs(_Fragment, { children: [closeBtn, _jsx(Button, { variant: "secondary", onClick: onOpenLibrary, children: "Open Library" }), _jsx(Button, { variant: "primary", loading: armBusy, onClick: onArm, children: "Start recording" })] }));
        case 'armed':
            return (_jsxs(_Fragment, { children: [closeBtn, _jsx(Button, { variant: "secondary", onClick: onCancelArmed, children: "Cancel armed session" })] }));
        case 'recording':
        case 'stopping':
            return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "secondary", onClick: onClose, children: "Hide" }), _jsx(Button, { variant: "destructive", loading: stopBusy, onClick: onStop, children: "Stop recording" })] }));
        case 'uploading':
            return (_jsx(_Fragment, { children: _jsx(Button, { variant: "secondary", onClick: onClose, children: "Hide" }) }));
        case 'done':
            return (_jsxs(_Fragment, { children: [closeBtn, _jsx(Button, { variant: "secondary", onClick: onRecordAnother, children: "Record another" }), _jsx(Button, { variant: "primary", onClick: onViewFlow, children: "View in Flow" })] }));
        default: {
            const _exhaustive = phase;
            void _exhaustive;
            return closeBtn;
        }
    }
}
